import { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { toast } from '@/lib/toast';
import { Modal } from '@heroui/react';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { canIn, PERMISSIONS } from '@/lib/guildPermissions';
import { GROUP_DM_PERMISSIONS } from '@bunker/shared';
import {
  ChatBubbleIcon,
  CloseIcon,
  HashIcon,
  UsersIcon,
} from '@/components/icons';
import { emitAppEvent, onAppEvent, type ForwardPayload } from '@/lib/appEvents';
import { buildForwardBody } from '@/lib/forwarded';

/**
 * Forward a message to a channel or direct conversation — pick a target,
 * we send a *new* message there whose body quotes the original.
 *
 * Wire shape:
 *   - Opens via the typed `chat:forward-open` app event (see lib/appEvents).
 *     Detail carries the original message's display data; we don't need the
 *     message id because v1 forwards are just authored text (no server-side
 *     forwardedFrom column yet — see docs/DEPLOY-BLOCKERS §6).
 *   - On pick, the modal calls api.sendMessage on the target channel
 *     with a synthesized body: "↪ Переслано · @author из #channel"
 *     header + a blockquoted copy of the content. The markdown engine
 *     already renders blockquotes, so the receiving side gets the
 *     expected visual without any new renderer code.
 *   - After send, navigates to the target so the user lands where their
 *     forwarded message just appeared.
 *
 * Why not server-side forwarding (forwardedFrom on Message)?
 *   - Saves a migration round-trip; the visual / UX is already there.
 *   - Migrating later means adding the column + populating it for the
 *     new message; the body text from v1 is still valid (markdown-only).
 */
// ForwardPayload is declared in lib/appEvents to avoid circular imports.
export type { ForwardPayload } from '@/lib/appEvents';

export function ForwardModal() {
  const { t } = useTranslation();
  const [payload, setPayload] = useState<ForwardPayload | null>(null);
  const [query, setQuery] = useState('');
  const [sending, setSending] = useState(false);
  const open = payload !== null;

  useEffect(() => {
    return onAppEvent('chat:forward-open', (detail) => {
      if (!detail) return;
      setQuery('');
      setPayload(detail);
    });
  }, []);

  const candidates = useForwardCandidates(query, open, (payload?.attachmentIds?.length ?? 0) > 0);

  const selectGuild = useDataStore((s) => s.selectGuild);
  const selectChannel = useDataStore((s) => s.selectChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);

  async function commit(targetKey: string) {
    if (!payload || sending) return;
    const entry = candidates.find((c) => c.key === targetKey);
    if (!entry) return;
    setSending(true);
    const body = buildForwardBody(payload, i18n.t('forward.someone'));
    try {
      await api.sendMessage(entry.channelId, {
        content: body,
        ...(payload.attachmentIds && payload.attachmentIds.length > 0
          ? { forwardUploadIds: payload.attachmentIds, forwardFromMessageId: payload.messageId }
          : {}),
      });
      toast.success(t('forward.sent', { target: entry.label }));
      // Navigate to the target so the user sees their forward land.
      if (entry.kind === 'guild-channel') {
        if (entry.guildId) selectGuild(entry.guildId);
        selectChannel(entry.channelId);
      } else {
        selectDirectChannel(entry.channelId);
      }
      setPayload(null);
    } catch (err) {
      toast.error(...errToToast(err, t('forward.error')));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal.Backdrop
      className="app-viewport-overlay"
      isOpen={open}
      // Not closable mid-send (Esc / backdrop): the result must land somewhere
      // the user can see it — the toast and the jump to the target.
      onOpenChange={(o) => !o && !sending && setPayload(null)}
    >
      <Modal.Container className="app-panel-container" size="lg" placement="top">
        <Modal.Dialog aria-label={t('forward.dialogTitle')} className="app-panel-dialog app-search-dialog overflow-hidden p-0">
          {payload && (
            <div className="border-b border-separator px-5 py-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-ink">{t('messages.forward')}</div>
                <button type="button" aria-label={t('common.close')} disabled={sending} onClick={() => setPayload(null)}
                  className="grid size-11 shrink-0 place-items-center rounded-lg text-ink-muted transition-colors hover:bg-default/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50">
                  <CloseIcon className="size-4" />
                </button>
              </div>
              <div className="line-clamp-2 text-xs leading-snug text-ink-dim">
                <span className="text-ink">{payload.authorName}: </span>
                {payload.content || <span className="italic">{t('common.noText')}</span>}
              </div>
              {sending && <p role="status" className="mt-2 text-xs text-ink-muted">{t('forward.sending')}</p>}
            </div>
          )}
          <Command className="app-search-command" shouldFilter={false} label={t('forward.commandLabel')}>
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={t('forward.placeholder')}
              className="block w-full bg-transparent px-5 py-3.5 text-sm text-ink placeholder:text-ink-muted focus:outline-none"
              autoFocus
            />
            <div className="border-t border-separator" />
            <Command.List aria-busy={sending} className="app-panel-results scrollbar-thin min-h-0 overflow-y-auto py-1">
              {query && (
                <Command.Empty className="px-5 py-6 text-center text-sm text-ink-muted">
                  {t('forward.nothingFound')}
                </Command.Empty>
              )}
              {candidates.map((entry) => (
                <Command.Item
                  key={entry.key}
                  value={`${entry.label} ${entry.sublabel}`}
                  disabled={sending}
                  onSelect={() => void commit(entry.key)}
                  className={
                    'group flex min-h-11 w-full cursor-pointer items-center gap-3 px-5 py-2.5 text-left transition-colors ' +
                    'data-[selected=true]:bg-accent/8 data-[disabled=true]:cursor-wait data-[disabled=true]:opacity-60'
                  }
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-default/60 text-ink-muted">
                    {entry.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">
                      {entry.label}
                    </span>
                    <span className="block truncate text-xs text-ink-muted">
                      {entry.sublabel}
                    </span>
                  </span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );

}

/**
 * Synthesise the forwarded-message body. Header line + blockquote keeps
 * the original visually attributable without any new server schema. The
 * blockquote also renders nicely in clients that don't know about
 * "forwarded" yet (browser preview, email digest of pending push, etc).
 */
interface ForwardCandidate {
  key: string;
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  channelId: string;
  guildId: string | null;
  kind: 'guild-channel' | 'dm';
  score: number;
}

/**
 * Builds the list of forward targets. Picks TEXT/ANNOUNCE channels I may
 * post in (SEND_MESSAGES, plus ATTACH_FILES when the forward carries files)
 * — no voice, no read-only channels — and the DMs/groups I may write to.
 * Mirrors the
 * scoring rules from QuickSwitcher: prefix > substring, current guild
 * boosted; current channel intentionally suppressed (you wouldn't
 * forward into the same conversation).
 */
function useForwardCandidates(query: string, open: boolean, withFiles: boolean): ForwardCandidate[] {
  const { t } = useTranslation();
  const guilds = useDataStore((s) => s.guilds);
  const guildOrder = useDataStore((s) => s.guildOrder);
  const dmChannels = useDataStore((s) => s.dmChannels);
  const dmOrder = useDataStore((s) => s.dmOrder);
  const currentChannelId = useDataStore((s) => s.currentChannelId);
  const currentGuildId = useDataStore((s) => s.currentGuildId);
  const me = useAuthStore((s) => s.user);

  return useMemo(() => {
    if (!open) return [];
    const q = query.trim().toLowerCase();
    const out: ForwardCandidate[] = [];

    function rank(...names: (string | null | undefined)[]): {
      matched: boolean;
      score: number;
    } {
      if (!q) return { matched: true, score: 100 };
      let best = Infinity;
      for (let i = 0; i < names.length; i++) {
        const n = (names[i] ?? '').toLowerCase();
        if (!n) continue;
        if (n.startsWith(q)) best = Math.min(best, i);
        else if (n.includes(q)) best = Math.min(best, 10 + i);
      }
      return { matched: best !== Infinity, score: best };
    }

    // Guild text + announce channels
    for (const gid of guildOrder) {
      const g = guilds[gid];
      if (!g) continue;
      const boost = g.id === currentGuildId ? -1 : 0;
      for (const ch of g.channels) {
        if (ch.type !== 'TEXT' && ch.type !== 'ANNOUNCE') continue;
        if (ch.id === currentChannelId) continue;
        if (!canIn(ch, PERMISSIONS.SEND_MESSAGES)) continue;
        if (withFiles && !canIn(ch, PERMISSIONS.ATTACH_FILES)) continue;
        const r = rank(ch.name, g.name);
        if (!r.matched) continue;
        out.push({
          key: `c-${ch.id}`,
          label: `#${ch.name ?? '(unnamed)'}`,
          sublabel: g.name,
          icon: <HashIcon className="h-4 w-4" />,
          channelId: ch.id,
          guildId: g.id,
          kind: 'guild-channel',
          score: r.score + boost,
        });
      }
    }

    // DMs + group DMs
    for (const did of dmOrder) {
      const ch = dmChannels[did];
      if (!ch) continue;
      if (ch.id === currentChannelId) continue;
      if (ch.type === 'GROUP_DM') {
        const myRole = ch.directParticipants.find((p) => p.userId === me?.id)?.role ?? 'MEMBER';
        const memberMayWrite = ((ch.memberPermissions ?? 63) & GROUP_DM_PERMISSIONS.SEND_MESSAGES) !== 0;
        if (myRole === 'MEMBER' && !memberMayWrite) continue;
      }
      const others = ch.directParticipants.filter((p) => p.userId !== me?.id);
      const namePieces = others.map((p) => p.user.displayName || p.user.username);
      const label =
        ch.type === 'GROUP_DM'
          ? ch.name || namePieces.slice(0, 3).join(', ') || t('forward.groupFallback')
          : others[0]?.user.displayName || others[0]?.user.username || t('forward.dmFallback');
      const r = rank(label, others[0]?.user.username, ...namePieces);
      if (!r.matched) continue;
      out.push({
        key: `d-${ch.id}`,
        label,
        sublabel:
          ch.type === 'GROUP_DM'
            ? `${t('forward.groupFallback')} · ${t('header.members', { count: ch.directParticipants.length })}`
            : others[0]?.user.username
              ? `@${others[0].user.username}`
              : t('dm.title'),
        icon:
          ch.type === 'GROUP_DM' ? (
            <UsersIcon className="h-4 w-4" />
          ) : others[0]?.user.avatarUrl ? (
            <img
              src={others[0].user.avatarUrl}
              alt=""
              className="h-7 w-7 rounded-full object-cover"
            />
          ) : (
            <ChatBubbleIcon className="h-4 w-4" />
          ),
        channelId: ch.id,
        guildId: null,
        kind: 'dm',
        score: r.score - 1,
      });
    }

    out.sort((a, b) => a.score - b.score);
    return out.slice(0, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is stable; deps are the scoring inputs
  }, [
    open,
    query,
    guilds,
    guildOrder,
    dmChannels,
    dmOrder,
    currentChannelId,
    currentGuildId,
    me?.id,
    withFiles,
  ]);
}

/**
 * Imperative entry point for openers (the message context menu).
 * Emits the typed app event so we don't have to prop-drill the modal
 * state through 4 layers of components.
 */
export function openForwardModal(payload: ForwardPayload): void {
  emitAppEvent('chat:forward-open', payload);
}
